import { ENTITY_CHANNELS, SHELL_CHANNELS } from "@brains/contracts";
import type { EntityDB } from "./db";
import type { EmbeddingDB } from "./db/embedding-db";
import type {
  AssetTransaction,
  SqliteAssetRepository,
  StagedAsset,
} from "./sqlite-asset-repository";
import type {
  BaseEntity,
  EmbeddingJobData,
  EntityJobOptions,
  EntityMutationEventContext,
  EntityMutationResult,
  EmbeddingBackfillResult,
  EmbeddingIndexStats,
  EmbeddingFailureReference,
  StoreEmbeddingData,
  EntityEventBus,
  DeleteEntityRequest,
  CreateEntityRequest,
  UpdateEntityRequest,
  UpsertEntityRequest,
  EntityRegistry,
  EntityMutationAdmission,
} from "./types";
import type { EntitySerializer } from "./entity-serializer";
import type { EntityQueries } from "./entity-queries";
import type { ProjectionStore } from "./projection-store";
import type {
  EntityExportStore,
  EntityExportTransaction,
} from "./entity-export-store";
import type { IJobQueueService, JobInfo } from "@brains/job-queue";
import { createId } from "@brains/utils/id";
import type { Logger } from "@brains/utils/logger";
import { z } from "@brains/utils/zod";
import { computeContentHash } from "@brains/utils/hash";
import { entities } from "./schema/entities";
import { embeddings } from "./schema/embeddings";
import type { ProjectionChangedTarget } from "./schema/projection-state";
import { and, eq, sql } from "drizzle-orm";
import {
  entityWriteConditionSchema,
  EntityWriteConflictError,
} from "./entity-write-contracts";
import {
  assertEntityWriteCondition,
  type EntityWritePrecondition,
} from "./entity-write-state";
import { entityRevision, stableJson } from "./entity-revision";

const failedEmbeddingJobDataSchema = z.object({
  id: z.string().min(1),
  entityType: z.string().min(1),
  contentHash: z.string().min(1),
});

function parseEmbeddingFailureReference(
  job: JobInfo,
): EmbeddingFailureReference | null {
  try {
    const data = failedEmbeddingJobDataSchema.parse(JSON.parse(job.data));
    return {
      entityId: data.id,
      entityType: data.entityType,
      contentHash: data.contentHash,
    };
  } catch {
    // Job data we cannot read yields no reference; the caller treats that
    // the same as a job that named no entity.
    return null;
  }
}

function embeddingReferenceKey(reference: EmbeddingFailureReference): string {
  return `${reference.entityType}:${reference.entityId}:${reference.contentHash}`;
}

function isUniqueConstraintError(error: unknown): boolean {
  // Drizzle wraps the LibsqlError, so walk the cause chain
  for (let current = error; current instanceof Error; current = current.cause) {
    if (
      current.message.includes("UNIQUE constraint failed") ||
      current.message.includes("SQLITE_CONSTRAINT")
    ) {
      return true;
    }
  }
  return false;
}

class StaleEntityUpdateError extends Error {}

interface EmbeddingBackfillCandidate {
  id: string;
  entityType: string;
  contentHash: string;
}

interface EmbeddingBackfillCandidates extends EmbeddingIndexStats {
  tableMissing: boolean;
  skipped: number;
  rowsToBackfill: EmbeddingBackfillCandidate[];
}

export interface EntityMutationDeps {
  db: EntityDB;
  entityRegistry: EntityRegistry;
  entitySerializer: EntitySerializer;
  entityQueries: EntityQueries;
  jobQueueService: IJobQueueService;
  logger: Logger;
  messageBus?: EntityEventBus;
  mutationAdmission?: EntityMutationAdmission;
  projectionStore: ProjectionStore;
  assetRepository: SqliteAssetRepository;
  entityExportStore: EntityExportStore;
  projectionNow: () => number;
  /** Embedding DB for writes (separate from entity DB). */
  embeddingDb: EmbeddingDB;
  embeddingsEnabled: boolean;
}

/**
 * EntityMutations handles all write operations for entities
 * Extracted from EntityService for single responsibility
 */
export class EntityMutations {
  private db: EntityDB;
  private embeddingDb: EmbeddingDB;
  private entityRegistry: EntityRegistry;
  private entitySerializer: EntitySerializer;
  private entityQueries: EntityQueries;
  private jobQueueService: IJobQueueService;
  private messageBus?: EntityEventBus;
  private mutationAdmission?: EntityMutationAdmission;
  private readonly projectionStore: ProjectionStore;
  private readonly assetRepository: SqliteAssetRepository;
  private readonly entityExportStore: EntityExportStore;
  private readonly projectionNow: () => number;
  private readonly embeddingsEnabled: boolean;
  private projectionWakeup: (() => Promise<void>) | undefined;
  private logger: Logger;

  constructor(deps: EntityMutationDeps) {
    this.db = deps.db;
    this.embeddingDb = deps.embeddingDb;
    this.entityRegistry = deps.entityRegistry;
    this.entitySerializer = deps.entitySerializer;
    this.entityQueries = deps.entityQueries;
    this.jobQueueService = deps.jobQueueService;
    this.projectionStore = deps.projectionStore;
    this.assetRepository = deps.assetRepository;
    this.entityExportStore = deps.entityExportStore;
    this.projectionNow = deps.projectionNow;
    this.embeddingsEnabled = deps.embeddingsEnabled;
    this.logger = deps.logger.child("EntityMutations");
    if (deps.messageBus) {
      this.messageBus = deps.messageBus;
    }
    if (deps.mutationAdmission) {
      this.mutationAdmission = deps.mutationAdmission;
    }
  }

  public setProjectionWakeup(wakeup: () => Promise<void>): () => void {
    this.projectionWakeup = wakeup;
    let active = true;
    return (): void => {
      if (!active) return;
      active = false;
      if (this.projectionWakeup === wakeup) this.projectionWakeup = undefined;
    };
  }

  /**
   * Create a new entity (returns immediately, embedding generated in background)
   */
  public async createEntity<T extends BaseEntity>(
    request: CreateEntityRequest<T>,
  ): Promise<EntityMutationResult> {
    const { entity, options, preparedAsset } = request;
    options?.signal?.throwIfAborted();
    const condition =
      options?.conditionalWrite &&
      entityWriteConditionSchema.parse(options.conditionalWrite);
    if (
      condition &&
      (condition.expectedRevision !== null ||
        options.deduplicateId ||
        !entity.id)
    ) {
      throw new Error(
        "Conditional creation requires an explicit ID, an absent precondition, and no deduplication",
      );
    }
    const precondition: EntityWritePrecondition | undefined = condition
      ? {
          ...condition,
          entityType: entity.entityType,
          entityId: entity.id ?? "",
        }
      : undefined;
    this.logger.debug(
      `Creating entity asynchronously of type: ${entity["entityType"]}`,
    );

    // Generate ID, timestamps, and contentHash if not provided
    const now = new Date().toISOString();
    const entityWithDefaults = {
      ...entity,
      id: entity.id ?? createId(),
      created: entity.created ?? now,
      updated: entity.updated ?? now,
      contentHash: computeContentHash(entity.content),
    };

    // Validate entity against its schema
    const validatedEntity = this.entityRegistry.validateEntity(
      entity["entityType"],
      entityWithDefaults,
    );

    const persistValidator = this.entityRegistry.getPersistValidator(
      validatedEntity.entityType,
    );
    if (persistValidator) {
      await persistValidator(validatedEntity, { operation: "create" });
    }
    options?.signal?.throwIfAborted();

    // Prepare entity for storage
    const { markdown, metadata } =
      this.entitySerializer.prepareEntityForStorage(
        validatedEntity,
        validatedEntity.entityType,
      );

    // Compute contentHash from the serialized markdown
    const contentHash = computeContentHash(markdown);
    const stagedAsset = this.stageAsset(
      validatedEntity.entityType,
      validatedEntity.content,
      markdown,
      preparedAsset,
    );

    // Resolve final ID (may deduplicate on collision)
    let finalId = validatedEntity.id;
    if (options?.deduplicateId) {
      finalId = await this.resolveUniqueId(
        validatedEntity.id,
        validatedEntity.entityType,
      );
    }

    await this.mutationAdmission?.assertMutationAdmission({
      operation: "create",
      entityType: validatedEntity.entityType,
      entityId: finalId,
    });

    // Persist the entity, search row, and scheduler journal atomically.
    await this.projectionStore.withDirtyInput(
      {
        sourceType: validatedEntity.entityType,
        sourceId: finalId,
        revision: entityRevision({
          contentHash,
          metadata,
          visibility: validatedEntity.visibility,
        }),
        operation: "upsert",
        markedAt: this.projectionNow(),
      },
      async (transaction) => {
        if (precondition)
          await assertEntityWriteCondition(
            transaction,
            precondition,
            validatedEntity,
          );
        await this.bindAssetContent(
          transaction,
          validatedEntity.entityType,
          markdown,
          stagedAsset,
        );
        await options?.beforeWrite?.({
          ...validatedEntity,
          id: finalId,
          content: markdown,
          contentHash,
          metadata,
        });
        options?.signal?.throwIfAborted();
        // Once the entity write starts, settle the complete atomic mutation.
        await transaction.insert(entities).values({
          id: finalId,
          entityType: validatedEntity.entityType,
          content: markdown,
          contentHash,
          visibility: validatedEntity.visibility,
          metadata,
          created: new Date(validatedEntity.created).getTime(),
          updated: new Date(validatedEntity.updated).getTime(),
        });
        await this.syncFtsIndex(
          transaction,
          finalId,
          validatedEntity.entityType,
          markdown,
        );
        await this.persistEntityExport(
          transaction,
          {
            entityType: validatedEntity.entityType,
            entityId: finalId,
            operation: "upsert",
          },
          options?.persistenceOrigin,
        );
      },
    );
    await this.notifyProjectionScheduler();

    this.logger.debug(
      `Persisted entity ${validatedEntity.entityType}:${finalId} immediately`,
    );

    await this.emitEntityEvent(
      ENTITY_CHANNELS.created,
      validatedEntity.entityType,
      finalId,
      {
        ...validatedEntity,
        id: finalId,
      },
      undefined,
      options?.eventContext,
    );

    return this.enqueueEmbeddingJob({
      entityId: finalId,
      entityType: validatedEntity.entityType,
      contentHash,
      operation: "create",
      ...(options?.priority !== undefined && { priority: options.priority }),
      ...(options?.maxRetries !== undefined && {
        maxRetries: options.maxRetries,
      }),
      ...(options?.eventContext && { eventContext: options.eventContext }),
    });
  }

  /**
   * Update an existing entity (returns immediately, embedding generated in background)
   */
  public async updateEntity<T extends BaseEntity>(
    request: UpdateEntityRequest<T>,
  ): Promise<EntityMutationResult> {
    const { entity, options, preparedAsset } = request;
    options?.signal?.throwIfAborted();
    const condition =
      options?.conditionalWrite &&
      entityWriteConditionSchema.parse(options.conditionalWrite);
    if (
      condition &&
      (condition.expectedRevision === null ||
        options.expectedContentHash !== undefined)
    ) {
      throw new Error(
        "Conditional replacement requires a revision and cannot combine preconditions",
      );
    }
    const precondition: EntityWritePrecondition | undefined = condition
      ? { ...condition, entityType: entity.entityType, entityId: entity.id }
      : undefined;
    this.logger.debug(
      `Updating entity asynchronously: ${entity.entityType} with ID ${entity.id}`,
    );

    // Validate and serialize first to compute the new content hash
    const updatedEntity = {
      ...entity,
      updated: new Date().toISOString(),
      contentHash: computeContentHash(entity.content),
    };

    const validatedEntity = this.entityRegistry.validateEntity(
      entity.entityType,
      updatedEntity,
    );

    const persistValidator = this.entityRegistry.getPersistValidator(
      validatedEntity.entityType,
    );
    if (persistValidator) {
      await persistValidator(validatedEntity, { operation: "update" });
    }
    options?.signal?.throwIfAborted();

    const { markdown, metadata } =
      this.entitySerializer.prepareEntityForStorage(
        validatedEntity,
        validatedEntity.entityType,
      );

    const contentHash = computeContentHash(markdown);

    // Skip update only when all persisted fields are unchanged. Metadata-only
    // updates can leave serialized markdown/contentHash unchanged for adapters
    // that preserve frontmatter from content, but those DB metadata changes must
    // still persist for filtering/projections.
    const existing = await this.db
      .select({
        contentHash: entities.contentHash,
        visibility: entities.visibility,
        metadata: entities.metadata,
      })
      .from(entities)
      .where(
        and(
          eq(entities.id, validatedEntity.id),
          eq(entities.entityType, validatedEntity.entityType),
        ),
      )
      .limit(1);

    const existingEntity = existing.at(0);

    if (!existingEntity) {
      if (precondition)
        throw new EntityWriteConflictError(
          precondition.entityType,
          precondition.entityId,
        );
      throw new Error(
        `Entity not found: ${validatedEntity.entityType}:${validatedEntity.id}`,
      );
    }

    if (
      options?.expectedContentHash !== undefined &&
      existingEntity.contentHash !== options.expectedContentHash
    ) {
      this.logger.debug(
        `Skipping stale update for ${validatedEntity.entityType}:${validatedEntity.id}`,
      );
      return {
        entityId: validatedEntity.id,
        jobId: "",
        skipped: true,
        skipReason: "content-conflict",
      };
    }

    const stagedAsset = this.stageAsset(
      validatedEntity.entityType,
      validatedEntity.content,
      markdown,
      preparedAsset,
    );

    if (
      !precondition &&
      existingEntity.contentHash === contentHash &&
      existingEntity.visibility === validatedEntity.visibility &&
      stableJson(existingEntity.metadata) === stableJson(metadata)
    ) {
      await this.projectionStore.transferEntityAuthority(
        {
          entityType: validatedEntity.entityType,
          id: validatedEntity.id,
        },
        async (transaction) => {
          options?.signal?.throwIfAborted();
          await this.bindAssetContent(
            transaction,
            validatedEntity.entityType,
            markdown,
            stagedAsset,
          );
          options?.signal?.throwIfAborted();
          await this.pruneFtsIndexIfExcluded(
            transaction,
            validatedEntity.id,
            validatedEntity.entityType,
          );
          return this.persistEntityExport(
            transaction,
            {
              entityType: validatedEntity.entityType,
              entityId: validatedEntity.id,
              operation: "upsert",
            },
            options?.persistenceOrigin,
          );
        },
      );
      this.logger.debug(
        `Skipping no-op update for ${validatedEntity.entityType}:${validatedEntity.id}`,
      );
      if (options?.eventContext) {
        await this.emitEntityEvent(
          ENTITY_CHANNELS.updated,
          validatedEntity.entityType,
          validatedEntity.id,
          validatedEntity,
          existingEntity.metadata,
          options.eventContext,
        );
      }
      return { entityId: validatedEntity.id, jobId: "", skipped: true };
    }

    await this.mutationAdmission?.assertMutationAdmission({
      operation: "update",
      entityType: validatedEntity.entityType,
      entityId: validatedEntity.id,
    });

    try {
      await this.projectionStore.withDirtyInput(
        {
          sourceType: validatedEntity.entityType,
          sourceId: validatedEntity.id,
          revision: entityRevision({
            contentHash,
            metadata,
            visibility: validatedEntity.visibility,
          }),
          operation: "upsert",
          markedAt: this.projectionNow(),
        },
        async (transaction) => {
          if (precondition)
            await assertEntityWriteCondition(
              transaction,
              precondition,
              validatedEntity,
            );
          await this.bindAssetContent(
            transaction,
            validatedEntity.entityType,
            markdown,
            stagedAsset,
          );
          await options?.beforeWrite?.({
            ...validatedEntity,
            content: markdown,
            contentHash,
            metadata,
          });
          options?.signal?.throwIfAborted();
          // Cancellation after this boundary must not split the entity from its journals.
          const updateResult = await transaction
            .update(entities)
            .set({
              content: markdown,
              contentHash,
              visibility: validatedEntity.visibility,
              metadata,
              updated: new Date(validatedEntity.updated).getTime(),
            })
            .where(
              and(
                eq(entities.id, validatedEntity.id),
                eq(entities.entityType, validatedEntity.entityType),
                options?.expectedContentHash !== undefined
                  ? eq(entities.contentHash, options.expectedContentHash)
                  : undefined,
              ),
            );
          if (
            (condition || options?.expectedContentHash !== undefined) &&
            Number(updateResult.rowsAffected) === 0
          ) {
            if (precondition)
              throw new EntityWriteConflictError(
                precondition.entityType,
                precondition.entityId,
              );
            throw new StaleEntityUpdateError();
          }
          await this.syncFtsIndex(
            transaction,
            validatedEntity.id,
            validatedEntity.entityType,
            markdown,
          );
          await this.persistEntityExport(
            transaction,
            {
              entityType: validatedEntity.entityType,
              entityId: validatedEntity.id,
              operation: "upsert",
            },
            options?.persistenceOrigin,
          );
        },
      );
    } catch (error) {
      if (!(error instanceof StaleEntityUpdateError)) throw error;
      this.logger.debug(
        `Skipping concurrently stale update for ${validatedEntity.entityType}:${validatedEntity.id}`,
      );
      return {
        entityId: validatedEntity.id,
        jobId: "",
        skipped: true,
        skipReason: "content-conflict",
      };
    }
    await this.notifyProjectionScheduler();

    this.logger.debug(
      `Updated entity ${validatedEntity.entityType}:${validatedEntity.id} immediately`,
    );

    await this.emitEntityEvent(
      ENTITY_CHANNELS.updated,
      validatedEntity.entityType,
      validatedEntity.id,
      validatedEntity,
      // Prior metadata lets projections (e.g. series) reconcile a moved value
      // such as a changed `seriesName` without a full resync. Already loaded
      // above for the no-op check, so this adds no extra read.
      existingEntity.metadata,
      options?.eventContext,
    );

    return this.enqueueEmbeddingJob({
      entityId: validatedEntity.id,
      entityType: validatedEntity.entityType,
      contentHash,
      operation: "update",
      ...(options?.priority !== undefined && { priority: options.priority }),
      ...(options?.maxRetries !== undefined && {
        maxRetries: options.maxRetries,
      }),
      ...(options?.eventContext && { eventContext: options.eventContext }),
    });
  }

  /**
   * Delete an entity by type and ID
   */
  public async deleteEntity(request: DeleteEntityRequest): Promise<boolean> {
    const { entityType, id, options } = request;

    // Fetch prior entity so subscribers can gate on its metadata (e.g. the
    // `seriesName` field that drives the series projection). Without this,
    // every delete forces subscribers into a full resync because they can't
    // tell whether the deleted entity was relevant to them.
    const priorData = await this.entityQueries.getEntityData(entityType, id);
    const prior = priorData
      ? ((await this.entitySerializer.convertToEntity(priorData)) ?? undefined)
      : undefined;

    if (priorData) {
      await this.mutationAdmission?.assertMutationAdmission({
        operation: "delete",
        entityType,
        entityId: id,
      });
    }

    if (!priorData) return false;

    // Embeddings live in another database and are recoverable by backfill; the
    // entity row, FTS row, and scheduler journal share one atomic transaction.
    await this.embeddingDb
      .delete(embeddings)
      .where(
        and(eq(embeddings.entityType, entityType), eq(embeddings.entityId, id)),
      );
    await this.projectionStore.withDirtyInput(
      {
        sourceType: entityType,
        sourceId: id,
        revision: `deleted:${entityRevision({
          contentHash: priorData.contentHash,
          metadata: priorData.metadata,
          visibility: priorData.visibility,
        })}`,
        operation: "delete",
        markedAt: this.projectionNow(),
      },
      async (transaction) => {
        await transaction.run(
          sql`DELETE FROM entity_fts WHERE entity_id = ${id} AND entity_type = ${entityType}`,
        );
        await transaction
          .delete(entities)
          .where(and(eq(entities.entityType, entityType), eq(entities.id, id)));
        await this.persistEntityExport(
          transaction,
          { entityType, entityId: id, operation: "delete" },
          options?.persistenceOrigin,
        );
      },
    );
    await this.notifyProjectionScheduler();

    await this.emitEntityEvent(
      ENTITY_CHANNELS.deleted,
      entityType,
      id,
      prior,
      undefined,
      options?.eventContext,
    );

    return true;
  }

  /**
   * Create or update an entity based on existence
   */
  public async upsertEntity<T extends BaseEntity>(
    request: UpsertEntityRequest<T>,
  ): Promise<EntityMutationResult & { created: boolean }> {
    const { entity, options, preparedAsset } = request;
    this.logger.debug(
      `Upserting entity of type ${entity.entityType} with ID ${entity.id}`,
    );

    const exists = await this.entityQueries.entityExists(
      entity.entityType,
      entity.id,
    );

    if (exists) {
      const result = await this.updateEntity({
        entity,
        ...(preparedAsset !== undefined && { preparedAsset }),
        ...(options !== undefined && { options }),
      });
      return { ...result, created: false };
    }

    try {
      const result = await this.createEntity({
        entity,
        ...(preparedAsset !== undefined && { preparedAsset }),
        ...(options !== undefined && { options }),
      });
      return { ...result, created: true };
    } catch (error) {
      // A concurrent create can win between the existence check and the
      // insert — fall through to the update path instead of surfacing the
      // raw unique-constraint violation.
      if (!isUniqueConstraintError(error)) {
        throw error;
      }
      this.logger.debug(
        `Entity ${entity.entityType}:${entity.id} was created concurrently, updating instead`,
      );
      const result = await this.updateEntity({
        entity,
        ...(preparedAsset !== undefined && { preparedAsset }),
        ...(options !== undefined && { options }),
      });
      return { ...result, created: false };
    }
  }

  /** Reconcile indexes and lifecycle subscribers after atomic projection writes. */
  public async reconcileProjectionTargets(
    targets: readonly ProjectionChangedTarget[],
  ): Promise<void> {
    await Promise.all(
      targets.map(async (target) => {
        if (target.operation === "delete") {
          await this.emitEntityEvent(
            ENTITY_CHANNELS.deleted,
            target.entityType,
            target.entityId,
          );
          await this.embeddingDb
            .delete(embeddings)
            .where(
              and(
                eq(embeddings.entityType, target.entityType),
                eq(embeddings.entityId, target.entityId),
              ),
            );
          return;
        }
        if (!target.contentHash) {
          throw new Error(
            `Projection target ${target.entityType}:${target.entityId} has no content hash`,
          );
        }
        await this.emitEntityEvent(
          ENTITY_CHANNELS.updated,
          target.entityType,
          target.entityId,
        );
        await this.enqueueEmbeddingJob({
          entityId: target.entityId,
          entityType: target.entityType,
          contentHash: target.contentHash,
          operation: "update",
        });
      }),
    );
  }

  /**
   * Store embedding for an entity
   * Used by embedding job handler to store embedding in the embeddings table
   * Entity must already exist in entities table
   */
  public async storeEmbedding(data: StoreEmbeddingData): Promise<void> {
    await this.embeddingDb
      .insert(embeddings)
      .values({
        entityId: data.entityId,
        entityType: data.entityType,
        embedding: data.embedding,
        contentHash: data.contentHash,
      })
      .onConflictDoUpdate({
        target: [embeddings.entityId, embeddings.entityType],
        set: {
          embedding: data.embedding,
          contentHash: data.contentHash,
        },
      });
  }

  public async backfillMissingEmbeddings(): Promise<EmbeddingBackfillResult> {
    if (!this.embeddingsEnabled) {
      this.logger.debug(
        "Skipping embedding backfill; semantic indexing is disabled",
      );
      return { queued: 0, skipped: 0 };
    }

    const candidates = await this.getEmbeddingBackfillCandidates();

    if (candidates.tableMissing) {
      this.logger.debug("Skipping embedding backfill; entities table missing");
      return { queued: 0, skipped: 0 };
    }

    let queued = 0;
    let skipped = candidates.skipped;

    for (const row of candidates.rowsToBackfill) {
      const result = await this.enqueueEmbeddingJob({
        entityId: row.id,
        entityType: row.entityType,
        contentHash: row.contentHash,
        operation: "update",
      });
      if (result.jobId) {
        queued++;
      } else {
        skipped++;
      }
    }

    return { queued, skipped };
  }

  public async getEmbeddingIndexStats(): Promise<EmbeddingIndexStats> {
    const candidates = await this.getEmbeddingBackfillCandidates();
    return {
      missingEmbeddings: candidates.missingEmbeddings,
      staleEmbeddings: candidates.staleEmbeddings,
      failedEmbeddings: candidates.failedEmbeddings,
      embeddableEntities: candidates.embeddableEntities,
      embeddedEntities: candidates.embeddedEntities,
    };
  }

  private async getEmbeddingBackfillCandidates(): Promise<EmbeddingBackfillCandidates> {
    if (!(await this.hasEntityTable())) {
      return {
        tableMissing: true,
        skipped: 0,
        rowsToBackfill: [],
        missingEmbeddings: 0,
        staleEmbeddings: 0,
        failedEmbeddings: 0,
        embeddableEntities: 0,
        embeddedEntities: 0,
      };
    }

    const failedEmbeddingKeys = await this.getFailedEmbeddingKeys();

    const entityRows = await this.db
      .select({
        id: entities.id,
        entityType: entities.entityType,
        contentHash: entities.contentHash,
      })
      .from(entities);

    const embeddingRows = await this.embeddingDb
      .select({
        entityId: embeddings.entityId,
        entityType: embeddings.entityType,
        contentHash: embeddings.contentHash,
      })
      .from(embeddings);

    const embeddingHashes = new Map<string, string>();
    for (const row of embeddingRows) {
      embeddingHashes.set(`${row.entityType}:${row.entityId}`, row.contentHash);
    }

    const rowsToBackfill: EmbeddingBackfillCandidate[] = [];
    let skipped = 0;
    let missingEmbeddings = 0;
    let staleEmbeddings = 0;
    let failedEmbeddings = 0;
    let embeddableEntities = 0;
    let embeddedEntities = 0;

    for (const row of entityRows) {
      const entityConfig = this.entityRegistry.getEntityTypeConfig(
        row.entityType,
      );
      if (entityConfig.embeddable === false) {
        skipped++;
        continue;
      }
      embeddableEntities++;

      const failureKey = embeddingReferenceKey({
        entityId: row.id,
        entityType: row.entityType,
        contentHash: row.contentHash,
      });
      const hasTerminalFailure = failedEmbeddingKeys.has(failureKey);
      const embeddingHash = embeddingHashes.get(`${row.entityType}:${row.id}`);
      if (embeddingHash === undefined) {
        if (hasTerminalFailure) {
          failedEmbeddings++;
          skipped++;
        } else {
          missingEmbeddings++;
          rowsToBackfill.push(row);
        }
        continue;
      }

      if (embeddingHash !== row.contentHash) {
        if (hasTerminalFailure) {
          failedEmbeddings++;
          skipped++;
        } else {
          staleEmbeddings++;
          rowsToBackfill.push(row);
        }
        continue;
      }

      embeddedEntities++;
      skipped++;
    }

    return {
      tableMissing: false,
      skipped,
      rowsToBackfill,
      missingEmbeddings,
      staleEmbeddings,
      failedEmbeddings,
      embeddableEntities,
      embeddedEntities,
    };
  }

  private async getFailedEmbeddingKeys(): Promise<Set<string>> {
    const failedJobs = await this.jobQueueService.getFailedJobs([
      SHELL_CHANNELS.embedding,
    ]);
    const failedEmbeddingKeys = new Set<string>();

    for (const job of failedJobs) {
      const reference = parseEmbeddingFailureReference(job);
      if (reference) {
        failedEmbeddingKeys.add(embeddingReferenceKey(reference));
      }
    }

    return failedEmbeddingKeys;
  }

  private async hasEntityTable(): Promise<boolean> {
    const rows = await this.db.all<{ name: string }>(
      sql`SELECT name FROM sqlite_master WHERE type = 'table' AND name = 'entities'`,
    );
    return rows.length > 0;
  }

  private stageAsset(
    entityType: string,
    entityContent: string,
    storedContent: string,
    preparedAsset: CreateEntityRequest<BaseEntity>["preparedAsset"],
  ): StagedAsset | undefined {
    const assetBacked =
      this.entityRegistry.getEntityTypeConfig(entityType).binaryStorage ===
      "asset";
    if (!preparedAsset) return undefined;
    if (!assetBacked) {
      throw new Error(
        `Entity type ${entityType} is not registered for asset-backed storage`,
      );
    }
    if (
      entityContent !== preparedAsset.ref ||
      storedContent !== preparedAsset.ref
    ) {
      throw new Error(
        `Prepared asset ${preparedAsset.ref} does not match canonical ${entityType} content`,
      );
    }
    return this.assetRepository.stage(preparedAsset);
  }

  private async bindAssetContent(
    transaction: AssetTransaction,
    entityType: string,
    storedContent: string,
    stagedAsset?: StagedAsset,
  ): Promise<void> {
    if (
      this.entityRegistry.getEntityTypeConfig(entityType).binaryStorage !==
      "asset"
    ) {
      return;
    }
    await this.assetRepository.bindEntityContent(
      transaction,
      storedContent,
      stagedAsset,
    );
  }

  /** Synchronize FTS independently from vector embedding policy. */
  private async syncFtsIndex(
    database: Pick<EntityDB, "run">,
    entityId: string,
    entityType: string,
    content: string,
  ): Promise<void> {
    await this.deleteFtsIndex(database, entityId, entityType);
    if (!this.isFullTextSearchable(entityType)) return;
    await database.run(
      sql`INSERT INTO entity_fts (entity_id, entity_type, content) VALUES (${entityId}, ${entityType}, ${content})`,
    );
  }

  private async pruneFtsIndexIfExcluded(
    database: Pick<EntityDB, "run">,
    entityId: string,
    entityType: string,
  ): Promise<void> {
    if (this.isFullTextSearchable(entityType)) return;
    await this.deleteFtsIndex(database, entityId, entityType);
  }

  private async deleteFtsIndex(
    database: Pick<EntityDB, "run">,
    entityId: string,
    entityType: string,
  ): Promise<void> {
    await database.run(
      sql`DELETE FROM entity_fts WHERE entity_id = ${entityId} AND entity_type = ${entityType}`,
    );
  }

  private isFullTextSearchable(entityType: string): boolean {
    return (
      this.entityRegistry.getEntityTypeConfig(entityType).fullTextSearchable !==
      false
    );
  }

  /**
   * Find a unique ID by appending -2, -3, etc. if the base ID already exists.
   */
  private async resolveUniqueId(
    baseId: string,
    entityType: string,
  ): Promise<string> {
    const exists = await this.entityQueries.entityExists(entityType, baseId);

    if (!exists) {
      return baseId;
    }

    // Try suffixes -2, -3, ... up to a reasonable limit
    for (let suffix = 2; suffix <= 100; suffix++) {
      const candidateId = `${baseId}-${suffix}`;
      const taken = await this.entityQueries.entityExists(
        entityType,
        candidateId,
      );

      if (!taken) {
        this.logger.debug(`Deduplicated entity ID: ${baseId} → ${candidateId}`);
        return candidateId;
      }
    }

    // Extremely unlikely fallback: append random suffix
    const fallbackId = `${baseId}-${createId().slice(0, 8)}`;
    this.logger.warn(
      `Could not deduplicate entity ID after 100 attempts, using random suffix: ${fallbackId}`,
    );
    return fallbackId;
  }

  public async wakeProjectionScheduler(): Promise<void> {
    await this.notifyProjectionScheduler();
  }

  private async notifyProjectionScheduler(): Promise<void> {
    try {
      await this.projectionWakeup?.();
    } catch (error) {
      // The durable journal remains pending for the next wakeup or restart.
      this.logger.error("Failed to wake projection scheduler", error);
    }
  }

  /**
   * Broadcast an entity lifecycle event via the message bus
   */
  private async emitEntityEvent(
    event: string,
    entityType: string,
    entityId: string,
    entity?: BaseEntity,
    previousMetadata?: BaseEntity["metadata"],
    eventContext?: EntityMutationEventContext,
  ): Promise<void> {
    if (!this.messageBus) {
      return;
    }

    this.logger.debug(`Emitting ${event} for ${entityType}:${entityId}`);

    const payload: Record<string, unknown> = {
      entityType,
      entityId,
      ...(eventContext?.conversationId
        ? { conversationId: eventContext.conversationId }
        : {}),
      ...(eventContext?.channelId ? { channelId: eventContext.channelId } : {}),
      ...(eventContext?.runId ? { runId: eventContext.runId } : {}),
      ...(eventContext?.toolCallId
        ? { toolCallId: eventContext.toolCallId }
        : {}),
      ...(eventContext?.actor ? { actor: eventContext.actor } : {}),
      ...(eventContext?.interfaceType
        ? { interfaceType: eventContext.interfaceType }
        : {}),
    };
    if (entity) {
      payload["entity"] = entity;
    }
    if (previousMetadata) {
      payload["previousMetadata"] = previousMetadata;
    }

    try {
      await this.messageBus.send({
        type: event,
        payload: payload,
        sender: "entity-service",
        broadcast: true,
      });
    } catch (error) {
      // Lifecycle delivery is a wake-up optimization. The durable export and
      // projection journals remain available for startup/periodic recovery.
      this.logger.error(
        `Failed to publish ${event} for ${entityType}:${entityId}`,
        error,
      );
    }
  }

  private async persistEntityExport(
    transaction: EntityExportTransaction,
    target: {
      entityType: string;
      entityId: string;
      operation: "upsert" | "delete";
    },
    origin: EntityJobOptions["persistenceOrigin"],
  ): Promise<void> {
    if (origin === "directory-sync") {
      await this.entityExportStore.clear(transaction, target);
      return;
    }
    await this.entityExportStore.record(transaction, {
      ...target,
      markedAt: this.projectionNow(),
    });
  }

  /**
   * Enqueue an embedding job, or return early if the entity type is non-embeddable
   */
  private async enqueueEmbeddingJob(
    params: Omit<EmbeddingJobData, "id"> &
      EntityJobOptions & { entityId: string },
  ): Promise<EntityMutationResult> {
    const {
      entityId,
      entityType,
      contentHash,
      operation,
      priority,
      maxRetries,
      eventContext,
    } = params;

    const entityConfig = this.entityRegistry.getEntityTypeConfig(entityType);
    if (!this.embeddingsEnabled || entityConfig.embeddable === false) {
      this.logger.debug(
        `Skipping embedding for ${
          this.embeddingsEnabled
            ? "non-embeddable entity type"
            : "disabled indexing"
        }: ${entityType}:${entityId}`,
      );
      return { entityId, jobId: "", skipped: false };
    }

    const jobData: EmbeddingJobData = {
      id: entityId,
      entityType,
      contentHash,
      operation,
    };

    const jobId = await this.jobQueueService.enqueue({
      type: SHELL_CHANNELS.embedding,
      data: jobData,
      options: {
        ...(priority !== undefined && { priority }),
        ...(maxRetries !== undefined && { maxRetries }),
        source: "entity-service",
        deduplication: "coalesce",
        deduplicationKey: `embedding:${entityType}:${entityId}:${contentHash}`,
        metadata: {
          operationType: "data_processing",
          operationTarget: entityId,
          ...(eventContext?.interfaceType
            ? {
                interfaceType: eventContext.interfaceType,
                requestedByInterface: eventContext.interfaceType,
              }
            : {}),
          ...(eventContext?.actor
            ? {
                requestedByActor: eventContext.actor,
                ...(eventContext.actor.kind === "user"
                  ? { requestedByUserId: eventContext.actor.userId }
                  : {}),
              }
            : {}),
          // Embedding jobs are background bookkeeping — suppress progress
          // and completion events (entity:embedding:ready covers consumers)
          silent: true,
        },
      },
    });

    this.logger.debug(
      `Queued embedding job for ${entityType}:${entityId} (job: ${jobId})`,
    );

    return { entityId, jobId, skipped: false };
  }
}
